import * as React from "react";
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  Divider,
  Button,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { onSocketEvent } from "../services/socket.service";
import { INotification } from "../services/api.types";
import { useAlert } from "../context/AlertContext";

export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  // Keep the latest setAlert in a ref so the socket subscription effect can run
  // once (empty deps) without capturing a stale closure.
  const setAlertRef = React.useRef(setAlert);
  React.useEffect(() => {
    setAlertRef.current = setAlert;
  }, [setAlert]);
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = React.useState<INotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const fetchNotifications = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admins/notifications");
      const data = res.data?.data;
      if (data) {
        setNotifications(data.notifications ?? []);
        setUnreadCount(data.unreadCount ?? 0);
      }
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount
  React.useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Real-time: prepend new notifications pushed by the server
  React.useEffect(() => {
    const unsub = onSocketEvent<INotification>("notification:new", (notif) => {
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
      // Surface an info toast for the incoming notification.
      const message = notif.body ? `${notif.title}: ${notif.body}` : notif.title;
      setAlertRef.current("info", message);
    });
    return unsub;
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
  };

  const handleClose = () => setAnchor(null);

  const handleMarkRead = async (id: string) => {
    try {
      await api.patch(`/admins/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // fail silently
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch("/admins/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // fail silently
    }
  };

  const handleNotificationClick = async (notif: INotification) => {
    if (!notif.isRead) await handleMarkRead(notif._id);
    // Navigate based on type
    if (notif.type === "new_pending_owner") {
      navigate("/dashboard/manage-owners");
    } else if (notif.type === "carwash_change_request") {
      navigate("/dashboard/manage-car-washes");
    }
    handleClose();
  };

  const open = Boolean(anchor);

  return (
    <>
      <Tooltip title={t("notifications.title")}>
        <IconButton color="inherit" onClick={handleOpen} sx={{ mr: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxHeight: 480, display: "flex", flexDirection: "column" } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("notifications.title")}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>
              {t("notifications.markAllRead")}
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary" variant="body2">
              {t("notifications.empty")}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflow: "auto", flex: 1 }}>
            {notifications.map((notif) => (
              <React.Fragment key={notif._id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notif)}
                    sx={{ bgcolor: notif.isRead ? "transparent" : "action.hover", py: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={notif.isRead ? 400 : 600}>
                          {notif.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {notif.body}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {new Date(notif.createdAt).toLocaleString()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
